import { omitBy, isNil, get } from 'lodash';
import { map, mapObjIndexed, omit, pipe, reject, pick, values } from 'ramda';
import { normalize } from 'normalizr';
import * as types from './constants';
import Http from '../../http';
import HttpV2 from '../../httpv2';
import * as schema from './schema';
import { resolveSeries } from './helpers';

export const fetchBrandsRequest = () => ({
  type: types.FETCH_BRANDS_REQUEST,
});

export const fetchBrandsSuccess = (brands) => ({
  type: types.FETCH_BRANDS_SUCCESS,
  response: normalize(brands, schema.arrayOfBrands),
});

export const saveBrandRequest = () => ({
  type: types.SAVE_BRAND_REQUEST,
});

export const deleteBrandRequest = () => ({
  type: types.DELETE_BRAND_REQUEST,
});

export const saveBrandSuccess = brand => ({
  type: types.SAVE_BRAND_SUCCESS,
  response: normalize(brand, schema.brand),
});

export const deleteBrandSuccess = brandId => ({
  type: types.DELETE_BRAND_SUCCESS,
  brandId,
});

export const fetchBrand = id => dispatch => Http.get(`/brand/${id}`, { include: 'shops,extensions,settings.customKioskLogo' })
  .then((res) => {
    dispatch(saveBrandSuccess(res));

    return res;
  });

const uploadCustomKioskLogo = (brandId, image) => {
  return Http.postRaw(`/brand/${brandId}/customKioskLogo`, { image });
};

export const saveBrand = data =>
  (dispatch) => {
    dispatch(saveBrandRequest());
    let brandId = null;

    const settings = data.settings;
    const minifiedSettings = omit(['customKioskLogo'], settings);
    const payload = {
      ...data,
      settings: minifiedSettings,
    };

    return Http.post('/brand', payload)
      .then(async (brand) => {
        const customKioskLogo = get(settings, 'customKioskLogo', null);
        const customKioskLogoEnabled = get(settings, 'customKioskLogoEnabled', false);
        if (!customKioskLogoEnabled || !customKioskLogo) {
          return brand;
        }

        await uploadCustomKioskLogo(brand.id, customKioskLogo);
        return brand;
      })
      .then(brand => Http.get(`/brand/${brand.id}`, { include: 'shops,extensions,settings.customKioskLogo' }))
      .then((brand) => {
        brandId = brand.id;
        return dispatch(saveBrandSuccess(brand));
      })
      .then(() => brandId);
  };

export const deleteBrand = brandId =>
  (dispatch) => {
    dispatch(deleteBrandRequest());
    return Http.delete(`/brand/${brandId}`)
      .then(() => dispatch(deleteBrandSuccess(brandId)));
  };

export const addExtensionPhoto = (extensionId, image, field) =>
  Http.postRaw(`/extension/${extensionId}/photo`, { image, field });

export const addGoogleConfig = (extensionId, file, field) =>
  Http.postRaw(`/extension/${extensionId}/google-config`, { file, field });

export const addAppleConfig = (extensionId, file, field) =>
  Http.postRaw(`/extension/${extensionId}/apple-config`, { file, field });

export const updateBrand = (brandId, data) => (dispatch) => {
  const photoFields = [
    'appIconImage',
    'appIconForegroundImage',
    'googlePlayFeatureGraphicImage',
    'logoImage',
    'smallLogoImage',
    'backgroundImage',
    'brandLogoImage',
    'brandBackgroundImage',
  ];
  const googleConfigFiles = [
    'firebaseConfig',
    'iosFirebaseConfig',
    'serviceAccountKey',
  ];
  const appleConfigFiles = [
    'appStoreConnectApiKey',
  ];
  const brandNullableFields = ['smsOriginator'];
  const extentionNullableFields = [
    'logoLeading',
    'logoTrailing',
    'backgroundAlpha',
    'iTunesLink',
    'iTunesMarketingLink',
    'iTunesSupportLink',
    'googleClientId',
    'applicationId',
    'playStoreLink',
  ];

  const prepare = payload => pipe(
    omit([...photoFields, ...googleConfigFiles, ...appleConfigFiles]),
    reject(isNil),
  )(payload);

  const extension = data.extension || {};
  const brandNullables = pick(brandNullableFields, data);
  const extensionNullables = pick(extentionNullableFields, extension);
  const minifiedData = omit(['shops'], data);
  const { settings } = data;
  const minifiedSettings = omit(['customKioskLogo'], settings);

  const updateData = extension ? {
    ...omitBy(minifiedData, isNil),
    ...brandNullables,
    extension: { ...prepare(extension), ...extensionNullables },
    settings: minifiedSettings,
  } : {
    ...omitBy(minifiedData, isNil),
    ...brandNullables,
    settings: minifiedSettings,
  };

  return Http.put(`/brand/${brandId}`, updateData)
    .then(async (brand) => {
      if (!extension || !extension.id) {
        return brand;
      }

      const getRequestsFromExtension = pipe(
        pick(photoFields),
        map(file => ((file instanceof File) ? file : null)),
        reject(isNil),
        mapObjIndexed((file, photoField) => addExtensionPhoto(extension.id, file, photoField)),
        values,
      )(extension);

      await resolveSeries(getRequestsFromExtension);
      return brand;
    })
    .then(async (brand) => {
      const customKioskLogo = get(settings, 'customKioskLogo', null);
      const didKioskLogoChange = customKioskLogo instanceof File;

      const customKioskLogoEnabled = get(settings, 'customKioskLogoEnabled', false);

      if (!customKioskLogoEnabled || !didKioskLogoChange) {
        return brand;
      }

      await uploadCustomKioskLogo(brandId, customKioskLogo);
      return brand;
    })
    .then(async (brand) => {
      if (!extension || !extension.id) {
        return brand;
      }

      const getRequestsFromExtension = pipe(
        pick(googleConfigFiles),
        map(files => ((files && files[0] instanceof File) ? files[0] : null)),
        reject(isNil),
        mapObjIndexed((file, configFileField) => addGoogleConfig(extension.id, file, configFileField)),
        values,
      )(extension);

      await resolveSeries(getRequestsFromExtension);
      return brand;
    })
    .then(async (brand) => {
      if (!extension || !extension.id) {
        return brand;
      }

      const getRequestsFromExtension = pipe(
        pick(appleConfigFiles),
        map(files => ((files && files[0] instanceof File) ? files[0] : null)),
        reject(isNil),
        mapObjIndexed((file, configFileField) => addAppleConfig(extension.id, file, configFileField)),
        values,
      )(extension);

      await resolveSeries(getRequestsFromExtension);
      return brand;
    })
    .then(() => dispatch(fetchBrand(brandId)));
};

export const brandCustomersImport = (brandId, data) => dispatch =>
  Http.postRaw(`/brand/${brandId}/import-customers`, data);

export const deleteImportedBrandCustomers = brandId => dispatch =>
  Http.delete(`/brand/${brandId}/delete-customers`);

export const deleteAllBrandCustomers = brandId => dispatch =>
  Http.post('/jobs/wipe-customers', { ownerId: brandId, ownerType: 'brand' });

export const updateBrandCustomerReminderSettings = (brandId, data) => (dispatch) => {
  return Http.post(`/brand/${brandId}/customer-reminder-settings`, data);
};

export const brandGiftCardsImport = (brandId, file) => dispatch =>
  Http.postRaw(`/brand/${brandId}/import-gift-cards`, { file });

export const deleteImportedBrandGiftCards = brandId => dispatch =>
  Http.delete(`/brand/${brandId}/delete-gift-cards`);

export const brandProductsImport = (brandId, file) => () =>
  HttpV2.postRaw(`/brand/${brandId}/import-products`, { file });

export const deleteImportedBrandProducts = brandId => dispatch =>
  HttpV2.delete(`/brand/${brandId}/delete-products`);

export const brandServicesImport = (brandId, file) => () =>
  Http.postRaw(`/brand/${brandId}/import-services`, { file });

export const deleteImportedBrandServices = brandId => dispatch =>
  Http.delete(`/brand/${brandId}/delete-services`);
